"use client";

var __jsx = React.createElement;
import React, { useEffect, useMemo, useRef, useState } from "react";
import "styles/components/muscle-select-input.css";
import Icons from "@modules/icons";
import MuscleDropdown from "../dropdown";
import { cn } from "@lib/cn";
import useAfterInitEffect from "@lib/hooks/use-after-initialization-effect";
import useCssModules from "@lib/hooks/use-css-modules";
const nullValue = {
  key: "",
  value: "",
  group: {
    key: "",
    label: "",
    value: ""
  }
};
const MuscleSelectInput = _ref => {
  var _cssModules$globals$f, _cssModules$globals$f2;
  let {
    options = [],
    label = "",
    fullLengthDropdown = true,
    onSelectOption = () => {},
    defaultSelection = nullValue,
    fullLength = true,
    value,
    inlineInput = false,
    placeholder = "",
    addEmptyOption = false,
    nullLabel = undefined,
    disabled = false,
    required = false,
    inlineModeConfiguration: {
      addBorder = false,
      renderVertical = false,
      textClassName,
      textSize = "sm",
      labelClassname = "",
      labelTitleClassName = "",
      isolatedContainerClassName = ""
    } = {
      addBorder: false,
      renderVertical: false,
      textSize: "sm",
      labelClassname: "",
      labelTitleClassName: "",
      isolatedContainerClassName: ""
    },
    chevronColor,
    chevronSize,
    focusOptionsByTyping = false,
    valueIsInvalid = false,
    invalidValueMessage = "",
    optionClassName,
    maxDropdownHeight = 450,
    reserveErrorSpace = false,
    hideEmptyLabel = false,
    dataTestId,
    isOpen,
    onDropdownToggle,
    hideOptionLabel = false
  } = _ref;
  const buttonRef = useRef(null);
  const {
    0: dropdownIsOpen,
    1: setDropdownIsOpen
  } = useState(false);
  const {
    0: selectedOption,
    1: setSelectedOption
  } = useState(defaultSelection);
  const {
    0: focusedOptionIndex,
    1: setFocusedOptionIndex
  } = useState(0);
  const {
    cssModules
  } = useCssModules();
  const {
    inputContainerClassName,
    inputLabelClassName,
    selectedOptionClassName,
    selectArrowIconClassName
  } = (_cssModules$globals$f = cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.formBuilder) !== null && _cssModules$globals$f !== void 0 ? _cssModules$globals$f : {};
  const {
    selectOptionClassName,
    selectSelectedOptionClassName
  } = (_cssModules$globals$f2 = cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.filters) !== null && _cssModules$globals$f2 !== void 0 ? _cssModules$globals$f2 : {};
  let typingTimeout;
  const {
    0: typedString,
    1: setTypedString
  } = useState("");
  const isDropdownOpen = isOpen !== undefined ? isOpen : dropdownIsOpen;
  const handleDropdownToggle = open => {
    if (isOpen === undefined) {
      setDropdownIsOpen(open);
    }
    onDropdownToggle === null || onDropdownToggle === void 0 || onDropdownToggle(open);
  };
  useEffect(() => {
    if (value) {
      setSelectedOption(value);
    }
  }, [value]);
  useEffect(() => {
    if (dropdownIsOpen) {
      // Timeout waits to reference to be set in the DOM
      setTimeout(() => {
        var _document$getElementB;
        (_document$getElementB = document.getElementById(getOptionId(selectedOption))) === null || _document$getElementB === void 0 || _document$getElementB.focus();
        // setFocusedOptionIndex to selected option index
        let index = options.findIndex(option => {
          var _option$group, _selectedOption$group;
          return option.key === selectedOption.key && ((_option$group = option.group) === null || _option$group === void 0 ? void 0 : _option$group.key) === ((_selectedOption$group = selectedOption.group) === null || _selectedOption$group === void 0 ? void 0 : _selectedOption$group.key);
        });
        index = index === -1 ? 0 : index;
        setFocusedOptionIndex(index);
      }, 50);
    }
  }, [dropdownIsOpen]);
  useAfterInitEffect(() => {
    typingTimeout = setTimeout(() => {
      setTypedString("");
    }, 1000);
    if (typedString !== "") {
      // Find option by typed string
      let index = options.findIndex(option => option.value.toLowerCase().startsWith(typedString.toLowerCase()));
      if (index !== -1) {
        var _document$getElementB2;
        setFocusedOptionIndex(index);
        const option = options[index];
        (_document$getElementB2 = document.getElementById(getOptionId(option))) === null || _document$getElementB2 === void 0 || _document$getElementB2.focus();
      }
    }
  }, [typedString]);
  useAfterInitEffect(() => {
    setDropdownIsOpen(false);
  }, [selectedOption]);
  const {
    hasGroups,
    groupInfo,
    groupedOptions
  } = useMemo(() => {
    const hasGroups = options.some(option => option.group);
    const groupInfo = options.reduce((acc, option) => {
      if (option.group) {
        acc[option.group.key] = {
          value: option.group.key,
          label: option.group.label
        };
      }
      return acc;
    }, {});
    const groupedOptions = options.reduce((acc, option) => {
      var _option$group2;
      const group = ((_option$group2 = option.group) === null || _option$group2 === void 0 ? void 0 : _option$group2.key) || "default";
      if (!acc[group]) acc[group] = [];
      acc[group].push(option);
      return acc;
    }, {});
    return {
      hasGroups,
      groupInfo,
      groupedOptions
    };
  }, [options]);
  const renderOptions = () => {
    return Object.entries(groupedOptions).map(_ref2 => {
      let [group, opts] = _ref2;
      return __jsx("div", {
        key: group,
        "data-testid": dataTestId + "-group-" + group
      }, group !== "default" && __jsx("div", {
        className: "tx-psm-regular text-primary_text cursor-default muscle-select-option"
      }, groupInfo[group].label), opts.map((option, index) => renderOption(option, index, hasGroups)));
    });
  };
  const renderOption = (option, index, isGrouped) => {
    return __jsx("div", {
      key: option.key,
      id: getOptionId(option),
      "data-testid": dataTestId + "-option-" + index,
      className: cn(selectOptionClassName, optionClassName !== null && optionClassName !== void 0 ? optionClassName : "", optionIsSelected(option) ? selectSelectedOptionClassName : "", {
        "muscle-select-focused-option text-primary_text bg-white": focusedOptionIndex === index && !optionIsSelected(option),
        "pl-8 tx-psm-semiBold": isGrouped,
        'tx-psm-regular': !isGrouped && !optionIsSelected(option),
        'tx-psm-semiBold': !isGrouped && optionIsSelected(option)
      }),
      tabIndex: -1,
      onMouseOver: () => setFocusedOptionIndex(index),
      onClick: () => selectOption(option, index)
    }, index === -1 ? nullLabel !== null && nullLabel !== void 0 ? nullLabel : option.value : option.value);
  };
  const handleDropdownKeyEvent = event => {
    if (dropdownIsOpen) {
      if (event.key === "Enter") {
        selectOption(options[focusedOptionIndex], focusedOptionIndex);
      } else if (event.key === "Escape" || event.key === "Tab") {
        setDropdownIsOpen(false);
      } else if (event.key === "ArrowDown") {
        setFocusedOptionIndex(focusedOptionIndex === options.length - 1 ? 0 : focusedOptionIndex + 1);
      } else if (event.key === "ArrowUp") {
        setFocusedOptionIndex(focusedOptionIndex === 0 ? options.length - 1 : focusedOptionIndex - 1);
      } else if (event.key.match(/^[a-zA-Z0-9\s]$/) && focusOptionsByTyping) {
        event.preventDefault();
        clearTimeout(typingTimeout);
        setTypedString(typedString + event.key);
      }
    } else {
      if (event.key === "Enter" || event.key === " ") {
        setDropdownIsOpen(true);
      }
    }
  };
  const selectOption = (clickedOption, index) => {
    setSelectedOption(clickedOption);
    setFocusedOptionIndex(index);
    onSelectOption(clickedOption);
  };
  const optionIsSelected = option => {
    if (hasGroups) {
      var _selectedOption$group2, _option$group3;
      return selectedOption.key === option.key && ((_selectedOption$group2 = selectedOption.group) === null || _selectedOption$group2 === void 0 ? void 0 : _selectedOption$group2.key) === ((_option$group3 = option.group) === null || _option$group3 === void 0 ? void 0 : _option$group3.key);
    }
    return selectedOption.key === option.key;
  };
  const getOptionId = option => {
    if (hasGroups) {
      var _option$group4;
      return option.key + "-" + option.value + "-" + ((_option$group4 = option.group) === null || _option$group4 === void 0 ? void 0 : _option$group4.key);
    }
    return option.key + "-" + option.value;
  };
  return __jsx("div", {
    ref: buttonRef,
    "data-testid": dataTestId,
    className: cn("relative flex flex-col outline-none", {
      "w-full": fullLength
    }, {
      "w-fit": inlineInput && !fullLength
    }, {
      "min-w-form-input": !fullLength && !inlineInput
    }),
    onClick: () => !disabled ? handleDropdownToggle(!isDropdownOpen) : null,
    onKeyDown: handleDropdownKeyEvent
  }, !inlineInput && __jsx(React.Fragment, null, (!hideEmptyLabel || hideEmptyLabel && label) && __jsx("div", {
    className: cn(inputLabelClassName, labelTitleClassName)
  }, label && label + (required ? " *" : ""))), __jsx("div", {
    className: cn(inputContainerClassName, {
      "opacity-50": disabled
    }, {
      clickable: !disabled
    }, isolatedContainerClassName),
    "data-testid": dataTestId + "-select-box",
    tabIndex: 0
  }, __jsx("div", {
    className: cn('overflow-hidden w-full', {
      "muscle-select-vertical-content": renderVertical
    }, {
      "muscle-select-horizontal-content items-center": !renderVertical
    }, {
      "flex-1 h-[24px]": disabled
    })
  }, inlineInput && __jsx("div", {
    className: cn(labelTitleClassName || "tx-psm-regular text-primary_text")
  }, label + (required ? " *" : "")), __jsx("div", {
    className: "flex flex-row items-center justify-between w-full  md:gap-2"
  }, hideOptionLabel ? __jsx("div", {
    className: selectedOptionClassName
  }, nullLabel || placeholder || "") : __jsx("div", {
    className: selectedOptionClassName
  }, selectedOption.value), __jsx(Icons.chevronDown, {
    className: selectArrowIconClassName,
    strokeWidth: 0.3
  })))), __jsx(MuscleDropdown, {
    buttonRef: buttonRef,
    isOpenState: isDropdownOpen,
    setIsOpenState: handleDropdownToggle,
    fullLength: fullLengthDropdown,
    alignment: "left",
    dataTestId: dataTestId
  }, __jsx("div", {
    id: "muscle-select-options",
    className: "muscle-select-options",
    "data-testid": dataTestId + "-div",
    style: {
      maxHeight: maxDropdownHeight
    }
  }, addEmptyOption && !hasGroups && renderOption(nullValue, -1, false), renderOptions())), nullLabel && placeholder && !valueIsInvalid && __jsx("div", {
    className: "tx-pxs-light text-primary_text pt-1"
  }, placeholder), reserveErrorSpace && __jsx("div", {
    className: "tx-pxs-light text-error-main mt-1 text-start"
  }, invalidValueMessage && invalidValueMessage ? invalidValueMessage : '\u00A0'), !reserveErrorSpace && valueIsInvalid && invalidValueMessage && __jsx("div", {
    className: "tx-pxs-light text-error-main mt-1 text-start"
  }, invalidValueMessage));
};
export default MuscleSelectInput;